import DeleteAccount from "./pages/DeleteAccount";
import TermsGate from "./components/TermsGate";
import { isValueEnabled } from "./lib/features";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SportProvider } from "./context/SportContext";
import { FollowsProvider } from "./context/FollowsContext";
import { AppLayout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import { HitmaxHero } from "./components/HitmaxHero";
import { HitmaxWordmark } from "./components/HitmaxWordmark";
import { initNative, watchNetwork } from "./lib/native";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Stats from "./pages/Stats";
import Play from "./pages/Play";
import Profile from "./pages/Profile";
import AccountSettings from "./pages/AccountSettings";
import MatchSheet from "./pages/MatchSheet";
import LiveMatch from "./pages/LiveMatch";
import PredictionDetail from "./pages/PredictionDetail";
import Paywall from "./pages/Paywall";
import FollowedMatches from "./pages/FollowedMatches";

// Écrans secondaires (administration, paiement, parcours ponctuels) : chargés à la demande.
const AdminVip = lazy(() => import("./pages/AdminVip"));
const AdminModeration = lazy(() => import("./pages/AdminModeration"));
const AdminPartners = lazy(() => import("./pages/AdminPartners"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const PasserPro = lazy(() => import("./pages/PasserPro"));
const VipPage = lazy(() => import("./pages/VipPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const RulesGame = lazy(() => import("./pages/RulesGame"));
const LegalDoc = lazy(() => import("./pages/LegalDoc"));
const PartnerLanding = lazy(() => import("./pages/PartnerLanding"));
const Methode = lazy(() => import("./pages/Methode"));
const TrackRecord = lazy(() => import("./pages/TrackRecord"));
const ValueHistory = lazy(() => import("./pages/ValueHistory"));
const SportValueHistory = lazy(() => import("./pages/SportValueHistory"));
const ValuePage = lazy(() => import("./pages/ValuePage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const NbaCompare = lazy(() => import("./pages/NbaCompare"));
const TennisCompare = lazy(() => import("./pages/TennisCompare"));
const Scorers = lazy(() => import("./pages/Scorers"));
const Player = lazy(() => import("./pages/Player"));
const Club = lazy(() => import("./pages/Club"));
const TennisPlayer = lazy(() => import("./pages/TennisPlayer"));
const NbaTeam = lazy(() => import("./pages/NbaTeam"));
const LiveFixtureDetail = lazy(() => import("./pages/LiveFixtureDetail"));

function Splash() {
  return (
    <div className="min-h-screen">
      <HitmaxHero className="min-h-screen">
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse"><HitmaxWordmark size="text-5xl sm:text-6xl" /></div>
        </div>
      </HitmaxHero>
    </div>
  );
}

function Shell() {
  const { user } = useAuth();
  const location = useLocation();
  if (location.pathname === "/supprimer-compte") return <DeleteAccount />;
  if (user && sessionStorage.getItem("hitmax_delete_return") === "1") {
    sessionStorage.removeItem("hitmax_delete_return");
    return <Navigate to="/supprimer-compte" replace />;
  }
  // Landing partenaire /p/{code} : accessible quel que soit l'état d'auth
  // (visiteur non connecté qui scanne le QR, ou déjà connecté).
  if (window.location.pathname.startsWith("/p/")) {
    return (
      <Suspense fallback={null}><Routes>
        <Route path="/p/:code" element={<PartnerLanding />} />
      </Routes></Suspense>
    );
  }
  if (user === null) return <Splash />;
  if (user === false || typeof user !== "object") {
    return (
      <Suspense fallback={null}><Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reglement-tirage" element={<RulesGame />} />
        <Route path="/reglement-jeu" element={<Navigate to="/reglement-tirage" replace />} />
        <Route path="/legal" element={<LegalDoc doc="legal" />} />
        <Route path="/cgu" element={<LegalDoc doc="cgu" />} />
        <Route path="/privacy" element={<LegalDoc doc="privacy" />} />
        <Route path="/reglement-dojo" element={<LegalDoc doc="reglement-dojo" />} />
        <Route path="/regles-communaute" element={<LegalDoc doc="regles-communaute" />} />
        <Route path="/match/:id" element={<MatchSheet />} />
        <Route path="/paywall" element={<Paywall />} />
        <Route path="/buteurs" element={<Scorers />} />
        <Route path="/renouvellement" element={<Paywall />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes></Suspense>
    );
  }
  if (!user.onboarded) {
    return (
      <Suspense fallback={null}><Routes>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes></Suspense>
    );
  }
  return (
    <AppLayout>
      <ErrorBoundary>
        <Suspense fallback={null}><Routes>
        <Route path="/" element={<Home />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/play" element={<Play />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/compte" element={<AccountSettings />} />
        <Route path="/match/:id" element={<MatchSheet />} />
        <Route path="/live/:id" element={<LiveMatch />} />
        <Route path="/mes-matchs" element={<FollowedMatches />} />
        <Route path="/methode" element={<Methode />} />
        <Route path="/performance" element={<TrackRecord />} />
        <Route path="/player/:id" element={<Player />} />
        <Route path="/club/:id" element={<Club />} />
        <Route path="/tennis/player/:name" element={<TennisPlayer />} />
        <Route path="/basket/team/:id" element={<NbaTeam />} />
        <Route path="/basket/compare" element={<NbaCompare />} />
        <Route path="/tennis/compare" element={<TennisCompare />} />
        <Route path="/passer-pro" element={<PasserPro />} />
        <Route path="/paywall" element={<Paywall />} />
        <Route path="/buteurs" element={<Scorers />} />
        <Route path="/renouvellement" element={<Paywall />} />
        <Route path="/vip" element={<VipPage />} />
        <Route path="/vf/:id" element={<LiveFixtureDetail />} />
        <Route path="/value-history" element={isValueEnabled() ? <ValueHistory /> : <Navigate to="/" replace />} />
        <Route path="/basket-value-history" element={<SportValueHistory sport="basket" />} />
        <Route path="/tennis-value-history" element={<SportValueHistory sport="tennis" />} />
        <Route path="/value" element={isValueEnabled() ? <ValuePage /> : <Navigate to="/" replace />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/reglement-tirage" element={<RulesGame />} />
        <Route path="/reglement-jeu" element={<Navigate to="/reglement-tirage" replace />} />
        <Route path="/legal" element={<LegalDoc doc="legal" />} />
        <Route path="/cgu" element={<LegalDoc doc="cgu" />} />
        <Route path="/privacy" element={<LegalDoc doc="privacy" />} />
        <Route path="/reglement-dojo" element={<LegalDoc doc="reglement-dojo" />} />
        <Route path="/regles-communaute" element={<LegalDoc doc="regles-communaute" />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/prediction/:sport/:home/:away" element={<PredictionDetail />} />
        <Route path="/admin/vip" element={<AdminVip />} />
        <Route path="/admin/moderation" element={<AdminModeration />} />
        <Route path="/admin/partenaires" element={<AdminPartners />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/onboarding" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes></Suspense>
      </ErrorBoundary>
    </AppLayout>
  );
}

export default function App() {
  const [offline, setOffline] = React.useState(false);
  React.useEffect(() => {
    initNative();
    let cleanup = () => {};
    watchNetwork((connected) => setOffline(!connected)).then((fn) => { cleanup = fn; });
    return () => cleanup();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <SportProvider>
          <FollowsProvider>
            {/* Filet global : une erreur d'affichage (connexion, inscription, onboarding) montre un message
                et un bouton de retour au lieu d'un écran vide. */}
            <ErrorBoundary>
              <Shell />
            </ErrorBoundary>
            <TermsGate />
            <Toaster position="bottom-center" theme="dark" offset={80} />
            {offline && (
              <div className="hm-offline-banner" data-testid="offline-banner">
                Hors ligne — reconnexion en cours…
              </div>
            )}
          </FollowsProvider>
        </SportProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
